//OrderFlowMySupport.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from '../../redux/actions/authActions';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faComment, faHeadset } from '@fortawesome/free-solid-svg-icons';

const OrderFlowMySupport = () => {
  const authState = useSelector(state => state.authReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [activeTab, setActiveTab] = useState('feedback'); // 'feedback' or 'support'
  const [feedbacks, setFeedbacks] = useState([]);
  const [supports, setSupports] = useState([]);
  const [feedbackFallback, setFeedbackFallback] = useState(false);
  const [supportFallback, setSupportFallback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('general'); // 'general', 'bug', 'feature', etc.

  const [fetchData] = useFetch();
  const isLoggedIn = authState?.isLoggedIn || false;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/orderflow-home");
  };

  // Sample fallback data for feedbacks
  const sampleFeedbacks = [
    {
      _id: 'fallback-1',
      title: 'Sample Feedback #1',
      type: 'general',
      status: 'Pending',
      message: 'This is a sample feedback message to demonstrate fallback data when backend fetch fails.',
      dateCreated: new Date(Date.now() - 86400000).toISOString(), // Yesterday
      response: 'No response yet'
    },
    {
      _id: 'fallback-2',
      title: 'Sample Feedback #2',
      type: 'feature',
      status: 'Resolved',
      message: 'Suggestion for new feature in the app.',
      dateCreated: new Date(Date.now() - 2 * 86400000).toISOString(), // 2 days ago
      response: 'Thank you for your suggestion! We\'ll review it.'
    }
  ];

  // Sample fallback data for supports
  const sampleSupports = [
    {
      _id: 'fallback-support-1',
      title: 'Sample Support Request #1',
      type: 'bug',
      status: 'In Progress',
      message: 'Encountered a bug while placing an order.',
      dateCreated: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
      response: 'Our team is investigating the issue.'
    },
    {
      _id: 'fallback-support-2',
      title: 'Sample Support Request #2',
      type: 'urgent',
      status: 'Resolved',
      message: 'Payment not reflecting in account.',
      dateCreated: new Date(Date.now() - 3 * 86400000).toISOString(), // 3 days ago
      response: 'Payment has been credited. Please check your account.'
    }
  ];

  // Fetch feedbacks
  const fetchFeedbacks = useCallback(() => {
    setLoading(true);
    const config = { 
      url: "/feedback/list", 
      method: "post", 
      data: { userId },
      headers: { Authorization: authState.token } 
    };
    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        setFeedbacks(response?.feedbacks || []);  
        setFeedbackFallback(false);
      })
      .catch(error => {
        console.error("Error fetching feedbacks:", error);
        // Fallback to sample data on error
        setFeedbackFallback(true);
        setFeedbacks(sampleFeedbacks);
      })
      .finally(() => setLoading(false));
  }, [authState.token, fetchData, userId]);

  // Fetch supports
  const fetchSupports = useCallback(() => {
    setLoading(true);
    const config = { 
      url: "/support/list", 
      method: "post", 
      data: { userId },
      headers: { Authorization: authState.token } 
    };
    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        setSupports(response?.supports || []);  
        setSupportFallback(false);
      })
      .catch(error => {
        console.error("Error fetching supports:", error);
        // Fallback to sample data on error
        setSupportFallback(true);
        setSupports(sampleSupports);
      })
      .finally(() => setLoading(false));
  }, [authState.token, fetchData, userId]);

  // Submit new feedback/support
  const submitRequest = useCallback(() => {
    if (!newMessage.trim()) return;
    setSubmitting(true);
    const url = activeTab === 'feedback' ? '/feedback/create' : '/support/create';
    const config = { 
      url, 
      method: "post", 
      data: { userId, message: newMessage, type: messageType },
      headers: { Authorization: authState.token } 
    };
    fetchData(config, { showSuccessToast: true })
      .then(() => {
        setNewMessage('');
        if (activeTab === 'feedback') fetchFeedbacks();
        else fetchSupports();
      })
      .catch(error => {
        console.error("Error submitting request:", error);
        // Optionally, show a toast or alert for user, but no fallback needed for creation
      })
      .finally(() => setSubmitting(false));
  }, [activeTab, newMessage, messageType, authState.token, fetchData, userId, fetchFeedbacks, fetchSupports]);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchFeedbacks();
    fetchSupports();
  }, [isLoggedIn, fetchFeedbacks, fetchSupports]);

  const currentList = activeTab === 'feedback' ? feedbacks : supports;
  const isCurrentFallback = activeTab === 'feedback' ? feedbackFallback : supportFallback;
  const sortedList = [...currentList].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  const formatDateTime = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const icon = activeTab === 'feedback' ? faComment : faHeadset;

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-gray-100">
      <header className="sticky top-0 z-50 bg-black border border-cyan-400 px-4 py-2 flex flex-row items-center justify-between shadow-lg">
        <div className='flex flex-col items-center sm:items-start justify-start w-full sm:w-auto'>
          <h2 className='text-lg sm:text-xl lg:text-2xl font-bold text-yellow-200 text-center mb-1 sm:mb-2'>
            <i className="fas fa-shopping-cart text-lg sm:text-xl lg:text-2xl text-yellow-200 mr-2"></i>Khareedo
          </h2>
          <h2 className='text-xs sm:text-sm font-bold text-white text-center'>
            Feedback & Support
          </h2> 
        </div>  
        <div className="flex items-center">
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="text-xs text-left text-blue-300">{userName}</div>
                <button
                  onClick={handleLogout}
                  className="p-2 mt-2 rounded-md bg-blue-600 hover:bg-blue-700 transition"
                  title="Logout"
                >
                  ⏻
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-3 py-1 rounded-full border border-blue-700 text-sm text-blue-200 hover:bg-blue-900"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm hover:bg-blue-400"
                >
                  Register
                </Link>
              </>
            )}
          </div>
        
          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-blue-300 bg-blue-500 rounded"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </div>
      </header>
      
      {/* Mobile menu content */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black border-b border-cyan-400 px-4 py-2 animate-fadeIn">
          {isLoggedIn ? (
            <div className="flex flex-row items-center justify-center gap-3">
              <span className="text-blue-300 font-medium whitespace-nowrap h-7">
                Welcome, {userName}
              </span>
              <button
                onClick={handleLogout}
                className="rounded-md bg-blue-600 hover:bg-blue-700 w-7 h-7 flex items-center justify-center text-white"
                title="Logout"
              >
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex flex-row items-center justify-center gap-6">
              <Link to="/login" className="text-blue-300 hover:text-yellow-300">
                Login
              </Link>
              <span className="text-blue-500">|</span>
              <Link to="/signup" className="text-blue-300 hover:text-yellow-300">
                Register
              </Link>
            </div>
          )}
        </div>
      )}  

      <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="border border-blue-500 p-4 sm:p-6 bg-gray-800 rounded-lg shadow-lg space-y-6">
            {/* Tabs */}
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={() => setActiveTab('feedback')}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                  activeTab === 'feedback'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <FontAwesomeIcon icon={faComment} className="mr-2" /> Feedback
              </button>
              <button
                onClick={() => setActiveTab('support')}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                  activeTab === 'support'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <FontAwesomeIcon icon={faHeadset} className="mr-2" /> Support
              </button>
            </div>

            {/* Submit Form */}
            <div className="space-y-4">
              <h4 className="text-sm sm:text-base font-semibold text-yellow-200 text-center">
                Submit New {activeTab === 'feedback' ? 'Feedback' : 'Support Request'}
              </h4>
              <select
                value={messageType}
                onChange={(e) => setMessageType(e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-blue-500"
              >
                <option value="general">General</option>
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="urgent">Urgent Issue</option>
              </select>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={`Enter your ${activeTab} message...`}
                className="w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-blue-500 resize-none h-24"
                rows={4}
              />
              <button
                onClick={submitRequest}
                disabled={submitting || !newMessage.trim()}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : `Submit ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`}
              </button>
            </div>

            {/* List */}
            <div>
              <h3 className="text-sm sm:text-base font-semibold mb-4 text-yellow-200 text-center sm:text-left">
                Your Previous {activeTab === 'feedback' ? 'Feedbacks' : 'Support Requests'} ({sortedList.length})
              </h3>

              {isCurrentFallback && (
                <div className="bg-yellow-600 text-yellow-900 p-3 rounded-md mb-4 text-center text-sm font-medium border border-yellow-500">
                  <strong>Note:</strong> This is a sample list displayed because the backend service is temporarily unavailable (e.g., 404 error). Real data will appear once the service is restored.
                </div>
              )}

              {loading ? (
                <div className="flex justify-center py-10">
                  <Loader />
                </div>
              ) : sortedList.length === 0 ? (
                <div className="min-h-[200px] sm:min-h-[300px] flex items-center justify-center gap-4 text-yellow-300">
                  <h3 className="text-base sm:text-lg text-orange-400">No Records Found!</h3>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {sortedList.map((item, index) => (
                    <div
                      key={item._id}
                      className="bg-black p-3 sm:p-4 rounded-md shadow-md transition-all hover:bg-gray-600 border border-gray-600"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="text-white text-sm sm:text-base">
                          <span className="font-bold text-yellow-300">{index + 1}.</span> 
                          <span className="ml-1">{item.title || `Request #${index + 1}`}</span>
                        </div>

                        <Tooltip text="View Details" position="top">
                          <button
                            className="text-blue-500 hover:text-blue-300 w-10 self-end sm:self-auto p-1 rounded transition-colors"
                            onClick={() => setSelectedItemId(selectedItemId === item._id ? null : item._id)}
                          >
                            <FontAwesomeIcon icon={faEye} className="text-sm sm:text-base" />
                          </button>
                        </Tooltip>
                      </div>

                      {selectedItemId === item._id && (
                        <div className="bg-gray-800 text-white mt-3 sm:mt-4 p-3 sm:p-4 rounded-lg space-y-2 text-xs sm:text-sm">
                          <p className="text-yellow-200 text-base sm:text-lg font-semibold">{item.title || 'Untitled'}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left">
                            <p><strong>Type:</strong> {item.type}</p>
                            <p><strong>Status:</strong> {item.status || 'Pending'}</p>
                            <p><strong>Message:</strong> {item.message}</p>
                            <p><strong>Date:</strong> {formatDateTime(item.dateCreated)}</p>
                            <p><strong>Response:</strong> {item.response || 'No response yet'}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2a0f2f] border-t border-blue-800 text-blue-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default OrderFlowMySupport